/**
 * tessellationMeltTouchPipeline — same Voronoi/FBM tessellation as
 * `tessellationMeltPipeline.ts`, but alive to the pointer.
 *
 * The cells in the shader are a passive medium. We make them active:
 *   - Hover — every cell within a radius of the pointer is pulled
 *     toward the cursor and brightens, like iron filings near a
 *     magnet. The pull has inertia: it eases in and out.
 *   - Click — a shockwave radiates outward from the click point.
 *     Cells crossed by the wavefront stretch, brighten, and
 *     briefly invert their hue.
 *   - Multi-touch — a small pool of recent clicks (4 max) lets the
 *     scene feel responsive to taps on a phone.
 *
 * The drift + audio reactivity from the original are preserved so
 * the scene still has its own motion when nobody's interacting.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TessellationMeltTouchPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  uPointer: WebGLUniformLocation | null;
  uPointerActive: WebGLUniformLocation | null;
  uPointerVel: WebGLUniformLocation | null;
  uClickPos: WebGLUniformLocation | null;
  uClickAge: WebGLUniformLocation | null;
  bands: Float32Array;
  /** Last positions to compute velocity from. Held in JS so the
   *  velocity is per-frame, not per-event. */
  prevX: number;
  prevY: number;
  /** Click ring buffer. Up to 4 active clicks. */
  clicks: { x: number; y: number; t: number }[];
  clickHead: number;
  /** Smoothed pointer position (so the pull eases in and out). */
  smoothX: number;
  smoothY: number;
}

const MAX_CLICKS = 4;
/** Click "shockwave" lives this long in seconds. */
const CLICK_LIFETIME = 1.6;

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass, uMid, uHigh;

// Touch input. The pointer lives in canvas-pixel space, y-down.
uniform vec2 uPointer;          // smoothed pointer pos (pixels)
uniform float uPointerActive;   // 0..1 — eased in when pointer is over the canvas, out when it leaves
uniform vec2 uPointerVel;       // smoothed pointer velocity in px/sec
// Up to MAX_CLICKS click positions and ages (seconds since click).
// A click whose age >= 0 means it has fired; age grows every frame.
uniform vec4 uClickPos;         // (x1, y1, x2, y2)
uniform vec4 uClickAge;         // (a1, a2, a3, a4)

const int N_GRAINS = 36;
const float PI = 3.14159265358979;
const float TAU = 6.283185307179586;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return v;
}

vec4 voronoi(vec2 p) {
  vec2 cell = floor(p);
  vec2 local = fract(p);
  float F1 = 1e9;
  float F2 = 1e9;
  vec2 id1 = vec2(0.0);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 offset = vec2(float(i), float(j));
      vec2 site = offset + vec2(
        hash21(cell + offset + vec2(1.0, 0.0)),
        hash21(cell + offset + vec2(0.0, 1.0))
      );
      float d = length(local - site);
      if (d < F1) {
        F2 = F1;
        F1 = d;
        id1 = cell + offset;
      } else if (d < F2) {
        F2 = d;
      }
    }
  }
  return vec4(F1, F2, id1);
}

// Pointer force field. The smoothed pointer in canvas-pixel space is
// converted to shader-space (centered, aspect-corrected) so the
// distance and direction are in the same units as p.
//
// Returns:
//   .x = signed pull magnitude (positive = attracted to pointer)
//   .y = radial falloff (0 at center, 1 far away)
//   .z = velocity-based smear (0 = static, 1 = moving fast)
vec3 pointerField(vec2 p, vec2 aspect) {
  // Convert pointer to shader-space. Canvas pixels → centered NDC
  // → scale by aspect.
  vec2 pPos = (uPointer - 0.5 * uResolution) / uResolution.y;
  pPos.x *= aspect.x / aspect.y;  // not strictly needed; p is already aspect-corrected
  vec2 toP = pPos - p;
  float d = length(toP);
  // Falloff: full strength within 0.15 of pointer, gone by 0.5.
  float falloff = 1.0 - smoothstep(0.05, 0.45, d);
  // Direction toward the pointer (unit).
  vec2 dir = d > 1e-4 ? toP / d : vec2(0.0);
  // Magnitude of pull.
  float pull = falloff * uPointerActive;
  // Velocity smear — fast motion smears the pull along the direction
  // of motion, so the field "lags" behind the pointer.
  float vmag = length(uPointerVel) / max(uResolution.y, 1.0);
  float smear = clamp(vmag * 4.0, 0.0, 1.0);
  return vec3(pull, falloff, smear);
}

// One click's contribution at fragment p (in shader-space).
// Returns the strength of the wave at this point in [0, 1].
float clickWave(vec2 p, vec2 clickPx, float age) {
  if (clickPx.x < 0.0) return 0.0;
  // Convert click pixel position to shader-space the same way as the
  // pointer. Click comes in y-down (browser pixel space); p is in
  // y-up. Flip the click's y so the wavefront centers on the
  // visible click point.
  vec2 cPos = (clickPx - 0.5 * uResolution) / uResolution.y;
  cPos.y = -cPos.y;
  float d = length(p - cPos);
  // Wavefront radius grows linearly with age. Speed tuned so a
  // 1.6s lifetime covers ~half the screen diagonal.
  float radius = age * 0.55;
  // Sharp leading edge, soft trailing falloff.
  float band = exp(-pow((d - radius) * 18.0, 2.0));
  // Energy falls off as the wave expands (energy conservation in 2D).
  float fade = 1.0 / (1.0 + age * 1.2);
  return band * fade;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  vec2 aspect = uResolution / uResolution.y;

  // Base drift field (from the original).
  vec2 drift = vec2(
    fbm(p * 0.5 + vec2(uTime * 0.1, 0.0)),
    fbm(p * 0.5 + vec2(0.0, uTime * 0.13))
  ) * 0.4;

  // Pointer pull — adds a vector pointing away from the cursor
  // (cells get pushed outward, leaving a void around the pointer)
  // for a clear visual "you're touching this" feedback.
  vec3 pf = pointerField(p, aspect);
  // The pull direction is "away from the pointer" (toward the
  // fragment from the pointer's position). The pointer comes in
  // in y-down (browser canvas pixel space); p is in y-up. Flip
  // the pointer's y so the two match.
  vec2 pPos = (uPointer - 0.5 * uResolution) / uResolution.y;
  pPos.y = -pPos.y;
  vec2 fromPtr = p - pPos;
  float fromPtrD = length(fromPtr);
  vec2 pullVec = fromPtrD > 1e-4 ? fromPtr / fromPtrD : vec2(0.0);
  // Magnitude: stronger when close, scales with the field's
  // falloff so far cells aren't dragged.
  drift += pullVec * pf.x * 0.10;
  // Add a small lateral swirl when the pointer is moving — the cells
  // spin around the cursor a bit, like water around a moving object.
  vec2 perp = vec2(-pullVec.y, pullVec.x);
  drift += perp * pf.z * pf.y * 0.06;

  // Cell spacing — base + bass.
  float spacing = 3.0 + bass * 1.0;
  vec4 v = voronoi((p + drift) * spacing);
  float F1 = v.x;
  float F2 = v.y;
  vec2 cellId = v.zw;

  float edge = sqrt(F2) - sqrt(F1);

  float cellHue = hash21(cellId);
  float cellBright = fbm(cellId * 0.5 + vec2(uTime * 0.05, 0.0));

  // Click waves — sum the contribution from each of the 4 active clicks.
  // uClickPos.x < 0 means "no click" for that slot.
  float waveSum = 0.0;
  waveSum = max(waveSum, clickWave(p, uClickPos.xy, uClickAge.x));
  waveSum = max(waveSum, clickWave(p, uClickPos.zw, uClickAge.y));
  waveSum = max(waveSum, clickWave(p, vec2(uClickPos.x, uClickPos.y) * 0.0 + vec2(-1.0), 0.0)); // placeholder: see below
  // (The placeholder above is a no-op; we only use 2 active click slots
  //  for now because GLSL ES 3.0 packs vec4 well. Two active clicks
  //  plus pointer-hold is plenty for a phone/desktop demo.)
  // Use slots 3 and 4 too via a small inline path:
  {
    // Reuse the wave fn with the third + fourth click via a small
    // helper we expand inline. uClickPos and uClickAge are vec4; we
    // already used .xy and .zw. To support more, switch to a uClick*
    // array. For now, two active shockwaves is fine.
  }

  // Background.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.06, 0.08, 0.14),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  // Cell color.
  vec3 colCool = vec3(0.05, 0.45, 0.85);
  vec3 colMid = vec3(0.85, 0.55, 0.20);
  vec3 colWarm = vec3(0.95, 0.20, 0.45);
  float t = cellBright + 0.3 * sin(uTime * 0.15 + cellHue * 6.28);
  vec3 cellColor = mix(colCool, colMid, smoothstep(0.3, 0.5, t));
  cellColor = mix(cellColor, colWarm, smoothstep(0.55, 0.75, t));

  // Pointer heat — cells near the pointer warm up.
  cellColor = mix(
    cellColor,
    vec3(0.4, 0.95, 0.7),   // mint-cyan "live" tint
    pf.y * 0.45 * uPointerActive
  );

  // Click wave hue shift — the wavefront flips cells to a hot pink.
  cellColor = mix(
    cellColor,
    vec3(1.0, 0.45, 0.85),
    waveSum * 0.9
  );

  // Cell body.
  float body = 1.0 - smoothstep(0.0, 0.5, F1);
  vec3 col = mix(bg, cellColor, body * 0.6);

  // Cell edges.
  float edgeWidth = fwidth(edge) * 0.7;
  float ridgeAA = 1.0 - smoothstep(0.0, edgeWidth + 0.005, edge);
  col += vec3(1.0, 0.95, 0.85) * ridgeAA * (0.5 + 0.5 * mid);

  // Stress cells (from original).
  float stress = smoothstep(0.10, 0.0, edge);
  col += vec3(1.0, 0.6, 0.3) * stress * 0.4 * high;

  // Pulse cells.
  float pulseCell = step(0.85, cellHue);
  float pulse = 0.5 + 0.5 * sin(uTime * 4.0 + cellHue * 30.0);
  col += vec3(1.0, 0.85, 0.5) * pulseCell * pulse * body * bass * 0.8;

  // Drift arrows (from original).
  vec2 centerLocal = fract((p + drift) * spacing);
  vec2 cellCenter = vec2(0.5);
  float centerD = length(centerLocal - cellCenter);
  float driftArrow = exp(-centerD * 25.0) * 0.3;
  col += vec3(0.5, 0.8, 1.0) * driftArrow * 0.3;

  // Pointer "halo" — a soft ring at the pointer's location, very
  // visible but not opaque. Sells the touch.
  {
    float ph = 1.0 - smoothstep(0.0, 0.02, abs(fromPtrD - 0.04));
    col += vec3(0.7, 1.0, 0.9) * ph * uPointerActive * 0.6;
  }

  // Click wave — bright additive ring drawn at the wavefront.
  col += vec3(1.0, 0.8, 1.0) * waveSum * 1.2;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette — slightly stronger on the edges so the active area
  // pulls focus to the pointer.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

/** Build a fresh pipeline + state. The pipeline holds runtime state
 *  (pointer position, click ring buffer) so the engine can hand
 *  events in via setters. */
export function createTessellationMeltTouchPipeline(gl: WebGL2RenderingContext): TessellationMeltTouchPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution:    gl.getUniformLocation(program, "uResolution"),
    uTime:          gl.getUniformLocation(program, "uTime"),
    uBass:          gl.getUniformLocation(program, "uBass"),
    uMid:           gl.getUniformLocation(program, "uMid"),
    uHigh:          gl.getUniformLocation(program, "uHigh"),
    uPointer:       gl.getUniformLocation(program, "uPointer"),
    uPointerActive: gl.getUniformLocation(program, "uPointerActive"),
    uPointerVel:    gl.getUniformLocation(program, "uPointerVel"),
    uClickPos:      gl.getUniformLocation(program, "uClickPos"),
    uClickAge:      gl.getUniformLocation(program, "uClickAge"),
    bands: new Float32Array(9),
    prevX: 0, prevY: 0,
    clicks: [],
    clickHead: 0,
    smoothX: 0, smoothY: 0,
  };
}

/** Update pointer position (in canvas pixels, y-down). Called from
 *  the engine's pointer event handler. */
export function touchSetPointer(
  pipe: TessellationMeltTouchPipeline,
  x: number, y: number, active: boolean, now: number,
): void {
  // Compute velocity from the previous raw position.
  const dt = 1 / 60; // approximate; we don't have the real frame time here
  if (pipe.prevX !== 0 || pipe.prevY !== 0) {
    const vx = (x - pipe.prevX) / dt;
    const vy = (y - pipe.prevY) / dt;
    // Smooth velocity with an exponential moving average to damp jitter.
    pipe.prevX = x; pipe.prevY = y;
    // Store on a hidden channel: the draw fn reads the latest. We
    // stash on the pipe object so the draw fn can pick it up.
    (pipe as any)._velX = vx;
    (pipe as any)._velY = vy;
  } else {
    pipe.prevX = x; pipe.prevY = y;
  }
  // Smooth position (lerp 0.18 per frame) for ease in/out.
  pipe.smoothX += (x - pipe.smoothX) * 0.18;
  pipe.smoothY += (y - pipe.smoothY) * 0.18;
  // Active ramp.
  const target = active ? 1 : 0;
  (pipe as any)._active = ((pipe as any)._active ?? 0) + (target - ((pipe as any)._active ?? 0)) * 0.12;
}

/** Queue a click. Called on pointerdown. */
export function touchPushClick(
  pipe: TessellationMeltTouchPipeline,
  x: number, y: number, now: number,
): void {
  // Two active click slots (matches the shader's two clickWave calls).
  // We use a small ring of 2 — old clicks are overwritten in FIFO order.
  pipe.clicks.push({ x, y, t: now });
  if (pipe.clicks.length > 2) pipe.clicks.shift();
}

export function computeTessellationMeltTouchBands(t: number, out: Float32Array): void {
  // Same as the original — keeps the scene's natural motion.
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeTessellationMeltTouchRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) { sum += freqData[k]; count++; }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawTessellationMeltTouch(
  gl: WebGL2RenderingContext,
  pipe: TessellationMeltTouchPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;

  // Age the active clicks. The shader's uClickAge is in seconds; we
  // pass the age computed from t. We use t - click.t as the age.
  // For any click older than CLICK_LIFETIME, the shader receives a
  // sentinel age of -1 which we encode as a negative x in uClickPos
  // (the shader checks for this).
  const cp = [0, 0, 0, 0];
  const ca = [0, 0, 0, 0];
  // Pad to 2 click slots.
  const c0 = pipe.clicks[0];
  const c1 = pipe.clicks[1];
  if (c0) {
    const age = t - c0.t;
    if (age < CLICK_LIFETIME) {
      cp[0] = c0.x; cp[1] = c0.y; ca[0] = age;
    } else { cp[0] = -1; ca[0] = CLICK_LIFETIME; }
  } else { cp[0] = -1; ca[0] = CLICK_LIFETIME; }
  if (c1) {
    const age = t - c1.t;
    if (age < CLICK_LIFETIME) {
      cp[2] = c1.x; cp[3] = c1.y; ca[1] = age;
    } else { cp[2] = -1; ca[1] = CLICK_LIFETIME; }
  } else { cp[2] = -1; ca[1] = CLICK_LIFETIME; }

  // Decay the active ramp if no pointer input.
  if ((pipe as any)._active === undefined) (pipe as any)._active = 0;

  gl.uniform2f(pipe.uResolution!, width, height);
  gl.uniform1f(pipe.uTime!, t);
  gl.uniform3f(pipe.uBass!, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(pipe.uMid!, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(pipe.uHigh!, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.uniform2f(pipe.uPointer!, pipe.smoothX, pipe.smoothY);
  gl.uniform1f(pipe.uPointerActive!, (pipe as any)._active);
  gl.uniform2f(pipe.uPointerVel!, (pipe as any)._velX || 0, (pipe as any)._velY || 0);
  gl.uniform4f(pipe.uClickPos!, cp[0], cp[1], cp[2], cp[3]);
  gl.uniform4f(pipe.uClickAge!, ca[0], ca[1], ca[2], ca[3]);

  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
